import dateFnsAddDays from 'date-fns/addDays'
import dateFnsAddMonths from 'date-fns/addMonths'
import dateFnsAddYears from 'date-fns/addYears'
import dateFnsFormat from 'date-fns/format'
import dateFnsFormatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'
import dateFnsParse from 'date-fns/parse'
import dateFnsParseISO from 'date-fns/parseISO'
import dateFnsStartOfMonth from 'date-fns/startOfMonth'
import dateFnsSub from 'date-fns/sub'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInHours from 'date-fns/differenceInHours'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval'
import endOfDay from 'date-fns/endOfDay'
import formatISO from 'date-fns/formatISO'
import fromUnixTime from 'date-fns/fromUnixTime'
import getUnixTime from 'date-fns/getUnixTime'
import intervalToDuration from 'date-fns/intervalToDuration'
import isAfter from 'date-fns/isAfter'
import startOfDay from 'date-fns/startOfDay'
import startOfYear from 'date-fns/startOfYear'
import { enUS, es } from 'date-fns/locale'

import { DATE_FORMAT_FILTER, DATE_FORMAT_LONG_DATE_AND_TIME } from './constants'
import { ITimeTillDue } from './interface'

const allowedLocales: Record<string, Locale> = {
  en: enUS,
  es: es
}

const getLocale = (language: string): Locale => {
  return allowedLocales[language]
}

export const addDays = dateFnsAddDays

export const formatDate = (date = new Date(), dateFormat = DATE_FORMAT_FILTER, language = 'es'): string => {
  return dateFnsFormat(date, dateFormat, { locale: getLocale(language) })
}

export const formatEndDate = (date: Date): string => {
  const dateEnd = endOfDay(date)
  return formatISO(dateEnd)
}

export const formatStartDate = (date: Date): string => {
  const dateStart = startOfDay(date)
  return formatISO(dateStart)
}

export const formatISOComplete = (date = new Date()): string => formatISO(date)

export const formatISODate = (date = new Date()): string => formatISO(date, { representation: 'date' })

export const formatUnixTimeStampToDate = (timestamp: number, finalFormat: string, language: string): string => {
  if (!timestamp) {
    return 'Fecha inválida'
  }
  const date = fromUnixTime(timestamp)
  return formatDate(date, finalFormat, language)
}

export const formatToFullDateAndTime = (date = new Date(), language = 'es'): string => formatDate(date, 'PPp', language)

export const formatToMonthAndYear = (date = new Date(), language = 'es'): string =>
  formatDate(date, 'MMMM yyyy', language)

export const formatFromDateString = (
  dateString: string,
  finalFormat = DATE_FORMAT_LONG_DATE_AND_TIME,
  langCode = 'es'
): string => {
  const dateParsed = parseISO(dateString)
  return formatDate(dateParsed, finalFormat, langCode)
}

export const formatAndLocalizeFromUTC = (
  dateString: string,
  finalFormat = DATE_FORMAT_LONG_DATE_AND_TIME,
  langCode = 'es'
): string => {
  const dateStringWithTimezone = dateString + 'Z'
  return formatFromDateString(dateStringWithTimezone, finalFormat, langCode)
}

export const weekdaysShort = (language: string): string[] =>
  eachDayOfInterval({ end: new Date(2023, 2, 11), start: new Date(2023, 2, 5) }).map(day =>
    formatDate(day, 'EEE', language)
  )

export const months = (language: string): string[] =>
  eachMonthOfInterval({ end: new Date(2023, 11), start: new Date(2023, 0) }).map(month =>
    formatDate(month, 'MMMM', language)
  )

export const parse = (dateString: string, formatString: string, language = 'es'): Date =>
  dateFnsParse(dateString, formatString, new Date(), { locale: getLocale(language) })

export const parseISO = dateFnsParseISO

export const getTimestamp = (date = new Date()): number => {
  return getUnixTime(date)
}

export const getTimestampNumber = (date: Date): number => date.getTime() / 1000

export const getTimeTillDue = (due: string): ITimeTillDue => {
  const now = new Date()
  const end = new Date(due)

  const duration = intervalToDuration({
    end,
    start: now
  })

  const days = Math.floor(differenceInHours(end, now) / 24) | 0
  const hours = duration.hours || 0
  const minutes = duration.minutes || 0

  return {
    days,
    hours,
    minutes
  }
}

export const getFirstDayLastYear = (date = new Date()): Date => {
  const lastYear = dateFnsAddYears(date, -1)
  return startOfYear(lastYear)
}

export const getNextMonths = (date: Date, months: number): Date => {
  if (!date) return new Date()
  const monthsAfter = dateFnsAddMonths(date, months)
  const today = new Date()
  return monthsAfter >= today ? today : monthsAfter
}

export const getNextThreeMonths = (date: Date): Date => {
  if (!date) return new Date()
  const threeMonthsAfter = dateFnsAddMonths(date, 3)
  const today = new Date()
  return threeMonthsAfter >= today ? today : threeMonthsAfter
}

export const getDifferenceInDays = (laterDate: number | Date, earlierDate: number | Date): number =>
  differenceInDays(laterDate, earlierDate)

export const getDifferenceInMinutes = (laterDate: number | Date, earlierDate: number | Date): number =>
  differenceInMinutes(laterDate, earlierDate)

export const startOfMonth = dateFnsStartOfMonth

export const isAfterDate = (firstDate: Date | number, secondDate: Date | number): boolean =>
  isAfter(firstDate, secondDate)

/**
 * Converts a Date object to an ISO string without including milliseconds and then encodes it.
 *
 * @param {Date} date - The input Date object.
 * @returns {string} - The ISO string without milliseconds, encoded.
 *
 * @example
 * const currentDate = new Date();
 * const encodedString = toISOStringWithoutMilliseconds(currentDate);
 * console.log(encodedString);
 *
 * @note
 * The function removes milliseconds from the ISO string to ensure it does not include fractional seconds.
 */
export const toISOStringWithoutMilliseconds = (date: Date): string => {
  return encodeURIComponent(date.toISOString().replace(/\.\d+Z$/, ''))
}

/**
 * @description
 * Get the date corresponding to the number of days from today
 *
 * @param {number} days - The number of days from today
 * @returns {Date} - The date corresponding to the number of days from today
 *
 * @example
 * const date = getDateToDay(1)
 * console.log(date)
 *
 * @note
 * The function adds the number of days to the current date
 */
export const getDateToTheDay = (days: number): Date => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date
}

export const formatDistanceToNowStrict = (
  date = new Date(),
  options: {
    addSuffix?: boolean
    unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year'
    roundingMethod?: 'floor' | 'ceil' | 'round'
    locale: string
  }
): string => {
  const finalOptions = { ...options, locale: getLocale(options.locale ?? es) }
  return dateFnsFormatDistanceToNowStrict(date, finalOptions)
}

export const sub = dateFnsSub

export const toFormatISODate8601 = (date: Date): string => {
  const isoString = date.toISOString()

  return isoString.replace('T', ' ').substring(0, 19)
}

/**
 * Converts an ISO 8601 timestamp to a format without time zone information.
 * ISO 8601 timestamp example : '2024-07-07T23:59:59-05:00'
 * @param {string} timestamp - The ISO 8601 timestamp to format.
 * @returns {string} - The formatted timestamp as "YYYY-MM-DD HH:MM:SS".
 */
export const formatTimestampToLocalString = (stringDate: string): string => {
  // Split the timestamp into date and time parts
  const [datePart, timePart] = stringDate.split('T')

  // Adjust the time part to remove the time zone offset
  const timeWithoutOffset = timePart.split(/\-|\+/)[0]

  return `${datePart} ${timeWithoutOffset}`
}

/**
 * Converts an ISO 8601 timestamp to a format 'YYY-MM-DD'.
 * ISO 8601 timestamp example : '2024-07-07T23:59:59-05:00'
 * @param {string} timestamp - The ISO 8601 timestamp to format.
 * @returns {string} - The formatted timestamp as "YYYY-MM-DD".
 */
export const formatTimestampToOnlyDateString = (stringDate: string): string => {
  // Split the timestamp into date and time parts
  const [datePart] = stringDate.split('T')

  return datePart
}

export const formatStringDateToNewFormat = (dateString: string, currentFormat: string, targetFormat: string) => {
  const date = parse(dateString, currentFormat)
  return formatDate(date, targetFormat)
}
